/**
 * HICC cart drawer — selling plan changes + drawer focus trap / a11y.
 */
(function () {
  const PLAN_SELECTOR = '[data-hicc-cart-selling-plan]';
  const CART_TRAP_ID = 'CartDrawer';
  const NESTED_TRAP_PANELS = {
    discount_field: 'hicc-cart-discount-panel',
  };
  const FOCUSABLE =
    'button:not([disabled]), a[href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

  const cartTrap = {
    active: false,
    keydown: null,
    focusin: null,
  };

  function getCartRoot() {
    return document.getElementById(CART_TRAP_ID);
  }

  function getActiveTrapRoot() {
    const field = window.Alpine?.store('xCartHelper')?.openField;
    if (field && NESTED_TRAP_PANELS[field]) {
      const nested = document.getElementById(NESTED_TRAP_PANELS[field]);
      if (nested && nested.getClientRects().length > 0) return nested;
    }
    return getCartRoot();
  }

  function getTrapFocusables(root) {
    if (!root) return [];
    return Array.from(root.querySelectorAll(FOCUSABLE)).filter((el) => {
      if (el.getAttribute('aria-hidden') === 'true') return false;
      return el.getClientRects().length > 0;
    });
  }

  function getCartFocusables() {
    return getTrapFocusables(getActiveTrapRoot());
  }

  function syncMainInert(inert) {
    if (typeof window.hiccSyncPageInert === 'function') {
      window.hiccSyncPageInert();
      return;
    }
    const main = document.getElementById('MainContent');
    if (main) main.inert = Boolean(inert);
  }

  function releaseCartTrap() {
    if (!cartTrap.active) {
      syncMainInert(false);
      return;
    }
    if (cartTrap.keydown) document.removeEventListener('keydown', cartTrap.keydown, true);
    if (cartTrap.focusin) document.removeEventListener('focusin', cartTrap.focusin, true);
    cartTrap.keydown = null;
    cartTrap.focusin = null;
    cartTrap.active = false;
    syncMainInert(false);
  }

  function activateCartTrap(focusTargetId) {
    const root = getCartRoot();
    if (!root) return;

    releaseCartTrap();

    cartTrap.keydown = (event) => {
      if (event.code !== 'Tab' || !window.Alpine?.store('xMiniCart')?.open) return;
      const trapRoot = getActiveTrapRoot();
      if (!trapRoot) return;
      const focusables = getTrapFocusables(trapRoot);
      if (focusables.length < 1) return;

      const first = focusables[0];
      const last = focusables[focusables.length - 1];
      const active = document.activeElement;

      if (event.shiftKey) {
        if (active === first || !trapRoot.contains(active)) {
          event.preventDefault();
          last.focus({ preventScroll: true });
        }
        return;
      }

      if (active === last || !trapRoot.contains(active)) {
        event.preventDefault();
        first.focus({ preventScroll: true });
      }
    };

    cartTrap.focusin = (event) => {
      if (!window.Alpine?.store('xMiniCart')?.open) return;
      const trapRoot = getActiveTrapRoot();
      if (!trapRoot || trapRoot.contains(event.target)) return;
      event.stopImmediatePropagation();
      getTrapFocusables(trapRoot)[0]?.focus({ preventScroll: true });
    };

    document.addEventListener('keydown', cartTrap.keydown, true);
    document.addEventListener('focusin', cartTrap.focusin, true);
    cartTrap.active = true;
    syncMainInert(true);

    const trapRoot = getActiveTrapRoot();
    const focusables = getTrapFocusables(trapRoot);
    const explicit = focusTargetId ? document.getElementById(focusTargetId) : null;
    const target =
      explicit && trapRoot?.contains(explicit) ? explicit : focusables[0];
    target?.focus({ preventScroll: true });
  }

  function setupCartA11y() {
    if (!window.Alpine) return;

    const focusStore = Alpine.store('xFocusElement');
    if (focusStore && !focusStore._hiccCartTrapPatched) {
      const originalTrap = focusStore.trapFocus.bind(focusStore);
      const originalRemove = focusStore.removeTrapFocus.bind(focusStore);

      focusStore.trapFocus = function hiccCartTrapFocus(containerId, elementFocusId) {
        if (containerId === CART_TRAP_ID) {
          activateCartTrap(elementFocusId);
          return;
        }
        releaseCartTrap();
        originalTrap(containerId, elementFocusId);
      };

      focusStore.removeTrapFocus = function hiccCartRemoveTrapFocus(elementToFocus) {
        releaseCartTrap();
        originalRemove(elementToFocus);
      };

      focusStore._hiccCartTrapPatched = true;
    }

    const miniCart = Alpine.store('xMiniCart');
    if (miniCart && !miniCart._hiccHideCartPatched) {
      const originalHideCart = miniCart.hideCart.bind(miniCart);
      miniCart.hideCart = function hiccHideCart() {
        releaseCartTrap();
        if (Alpine.store('xModal')?.focused) {
          Alpine.store('xModal').removeFocus();
        }
        originalHideCart();
      };
      miniCart._hiccHideCartPatched = true;
    }

    if (typeof Alpine.effect === 'function' && !window._hiccCartInertEffect) {
      Alpine.effect(() => {
        if (!Alpine.store('xMiniCart')?.open) {
          releaseCartTrap();
        }
      });
      window._hiccCartInertEffect = true;
    }

    if (typeof Alpine.effect === 'function' && !window._hiccNestedPanelEffect) {
      Alpine.effect(() => {
        const cartOpen = Alpine.store('xMiniCart')?.open;
        const field = Alpine.store('xCartHelper')?.openField;
        if (!cartOpen || field !== 'discount_field') return;
        requestAnimationFrame(() => {
          const panel = document.getElementById('hicc-cart-discount-panel');
          const input = document.getElementById('x-cart-discount-field');
          if (panel && input && panel.contains(input)) {
            input.focus({ preventScroll: true });
          }
        });
      });
      window._hiccNestedPanelEffect = true;
    }
  }

  function showLineError(itemKey, message) {
    const lineItemError = document.getElementById(`LineItemError-${itemKey}`);
    if (!lineItemError) return;
    lineItemError.classList.remove('hidden');
    const textEl = lineItemError.getElementsByClassName('cart-item__error-text')[0];
    if (textEl) textEl.innerHTML = message;
  }

  function hideLineError(itemKey) {
    const lineItemError = document.getElementById(`LineItemError-${itemKey}`);
    if (lineItemError) lineItemError.classList.add('hidden');
  }

  function syncPlanSelects(root) {
    const scope = root && root.querySelectorAll ? root : document;
    scope.querySelectorAll(PLAN_SELECTOR).forEach((select) => {
      select.dataset.currentPlan = select.value;
    });
  }

  function setupCartPlanObserver(root) {
    if (!root || root._hiccPlanObserver) return;
    root._hiccPlanObserver = new MutationObserver(() => syncPlanSelects(root));
    root._hiccPlanObserver.observe(root, { childList: true, subtree: true });
    syncPlanSelects(root);
  }

  function setupCartDrawerObserver() {
    setupCartPlanObserver(document.getElementById('CartDrawer'));
    setupCartPlanObserver(document.getElementById('hicc-cart-page'));
  }

  async function fetchAndRerenderSections(helper) {
    const sections = helper.getSectionsToRender().map((s) => s.id);
    if (!sections.length) return;

    if (sections.length > 4) {
      const results = await Promise.all(
        sections.map((id) =>
          fetch(`${window.location.pathname}?sections=${id}`).then((res) => res.json())
        )
      );
      helper.reRenderSections(Object.assign({}, ...results));
      return;
    }

    const res = await fetch(`${window.location.pathname}?sections=${sections.join(',')}`);
    const json = await res.json();
    helper.reRenderSections(json);
  }

  async function changeCartSellingPlan(select, previousValue) {
    if (!window.Alpine || !window.Shopify?.routes?.root) return;

    const itemKey = select.dataset.itemKey;
    const qty = select.dataset.quantity;
    const sellingPlanId = select.value;

    if (!itemKey || !sellingPlanId) return;
    if (String(previousValue) === String(sellingPlanId)) return;

    const helper = Alpine.store('xCartHelper');
    if (!helper) return;

    if (typeof helper.waitForCartUpdate === 'function') {
      await helper.waitForCartUpdate();
    }

    window.updatingCart = true;
    select.disabled = true;
    document.getElementById(`loading-${itemKey}`)?.classList.remove('hidden');
    hideLineError(itemKey);

    const updateData = {
      id: itemKey,
      quantity: parseInt(qty, 10) || 1,
      selling_plan: parseInt(sellingPlanId, 10),
      sections: helper.getSectionsToRender().map((s) => s.id),
      sections_url: window.location.pathname,
    };

    try {
      const response = await fetch(`${Shopify.routes.root}cart/change.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(updateData),
      });
      const parsed = await response.json();

      if (!response.ok || parsed.status === 422) {
        select.value = previousValue;
        showLineError(
          itemKey,
          parsed.message || parsed.description || 'Unable to update subscription.'
        );
        return;
      }

      if (parsed.sections) {
        helper.reRenderSections(parsed.sections);
      } else {
        await fetchAndRerenderSections(helper);
      }

      const bubble = document.querySelector('#cart-icon-bubble span');
      if (bubble && typeof parsed.item_count === 'number') {
        helper.currentItemCount = parsed.item_count;
        bubble.innerHTML = parsed.item_count < 100 ? String(parsed.item_count) : '99+';
      }

      select.dataset.currentPlan = sellingPlanId;
      document.dispatchEvent(new CustomEvent('eurus:cart:items-changed'));
    } catch (err) {
      select.value = previousValue;
      showLineError(itemKey, err?.message || 'Unable to update subscription.');
      console.error('[hicc-cart] selling plan change failed', err);
    } finally {
      window.updatingCart = false;
      select.disabled = false;
      document.getElementById(`loading-${itemKey}`)?.classList.add('hidden');
      delete select.dataset.planBeforeChange;
    }
  }

  document.addEventListener(
    'focusin',
    (event) => {
      const select = event.target.closest?.(PLAN_SELECTOR);
      if (select) {
        select.dataset.planBeforeChange = select.value;
      }
    },
    true
  );

  document.addEventListener('change', (event) => {
    const select = event.target.closest?.(PLAN_SELECTOR);
    if (!select) return;

    const previousValue =
      select.dataset.planBeforeChange ?? select.dataset.currentPlan ?? select.value;

    changeCartSellingPlan(select, previousValue);
  });

  function init() {
    setupCartDrawerObserver();
    syncPlanSelects();
    setupCartA11y();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  document.addEventListener('alpine:init', setupCartA11y);

  document.addEventListener('eurus:cart:items-changed', () => {
    requestAnimationFrame(init);
  });

  window.addEventListener('pageshow', () => {
    requestAnimationFrame(init);
  });
})();
